import { BaseStore } from "@omni/types/enums/baseStore.enum";
import { Environment } from "@omni/types/enums/environment.enum";
import { DeepPartial } from "@omni/types/utils/deepPartial";
import { ModelMapper } from "@omni/types/utils/modelMapper";
import { values } from "@omni/utils/object.utils";
import * as yup from "yup";

import { yupBaseStoreAware } from "../common/baseStoreAware.schema";

export type RawBffConfig = {
    // BaseStore agnostic
    APPLICATION_NAME: string;
    ENV: Environment;
    I18N_HOST: string;
    STORE_HOST: string;
    CART_HOST: string;
    CUSTOMER_HOST: string;
    PRODUCT_HOST: string;
    BAZAARVOICE_API_HOST: string;
    BAZAARVOICE_CLIENT_HOST: string;
    BAZAARVOICE_CALLBACK_URL: string;
    BAZAARVOICE_API_KEY: string;
    ALLOWED_ORIGINS: string[];
    TTL_SEARCH: number;
    TTL_RECOMMEND: number;
    REDIS_HOST: string;
    REDIS_PORT: number;
    REDIS_PW: string;
    REDIS_COMMAND_TIMEOUT: number;

    // BaseStore aware
    HUBO_ALGOLIA_APP_ID: string;
    HUBO_ALGOLIA_API_KEY: string;
    ETNA_ALGOLIA_APP_ID: string;
    ETNA_ALGOLIA_API_KEY: string;
    HUBO_REFERER: string;
    ETNA_REFERER: string;
    HUBO_REVIEWS_ENABLED: boolean;
    ETNA_REVIEWS_ENABLED: boolean;
};

const toBffConfig: ModelMapper<
    DeepPartial<RawBffConfig>,
    DeepPartial<BffConfig>
> = (config) => {
    return {
        // BaseStore agnostic
        applicationName: config.APPLICATION_NAME,
        environment: config.ENV,
        i18nHost: config.I18N_HOST,
        storeHost: config.STORE_HOST,
        cartHost: config.CART_HOST,
        customerHost: config.CUSTOMER_HOST,
        productHost: config.PRODUCT_HOST,
        bazaarvoiceApiHost: config.BAZAARVOICE_API_HOST,
        bazaarvoiceClientHost: config.BAZAARVOICE_CLIENT_HOST,
        bazaarvoiceCallbackUrl: config.BAZAARVOICE_CALLBACK_URL,
        bazaarvoiceApiKey: config.BAZAARVOICE_API_KEY,
        allowedOrigins: config.ALLOWED_ORIGINS,
        TTLSearch: config.TTL_SEARCH,
        TTLRecommend: config.TTL_RECOMMEND,
        redisHost: config.REDIS_HOST,
        redisPort: config.REDIS_PORT,
        redisPw: config.REDIS_PW,
        redisCommandTimeout: config.REDIS_COMMAND_TIMEOUT,

        // BaseStore aware
        algoliaAppId: {
            [BaseStore.Hubo]: config.HUBO_ALGOLIA_APP_ID,
            [BaseStore.Etna]: config.ETNA_ALGOLIA_APP_ID
        },
        algoliaApiKey: {
            [BaseStore.Hubo]: config.HUBO_ALGOLIA_API_KEY,
            [BaseStore.Etna]: config.ETNA_ALGOLIA_API_KEY
        },
        referer: {
            [BaseStore.Hubo]: config.HUBO_REFERER,
            [BaseStore.Etna]: config.ETNA_REFERER
        },
        featureToggles: {
            [BaseStore.Hubo]: {
                reviewsEnabled: config.HUBO_REVIEWS_ENABLED
            },
            [BaseStore.Etna]: {
                reviewsEnabled: config.ETNA_REVIEWS_ENABLED
            }
        }
    };
};

export const BffConfigSchema = yup
    .object()
    .transform((_, rawConfig: DeepPartial<RawBffConfig>) => {
        return toBffConfig(rawConfig);
    })
    .shape({
        // BaseStore agnostic
        applicationName: yup.string().required(),
        environment: yup
            .mixed<Environment>()
            .oneOf(values(Environment))
            .required(),
        cartHost: yup.string().required(),
        customerHost: yup.string().required(),
        productHost: yup.string().required(),
        i18nHost: yup.string().required(),
        storeHost: yup.string().required(),
        bazaarvoiceApiHost: yup.string().required(),
        bazaarvoiceClientHost: yup.string().required(),
        bazaarvoiceCallbackUrl: yup.string().required(),
        bazaarvoiceApiKey: yup.string().required(),
        allowedOrigins: yup
            .array()
            .of(yup.string().required())
            .json()
            .required(),
        TTLSearch: yup.number().required(),
        TTLRecommend: yup.number().required(),
        redisHost: yup.string().required(),
        redisPort: yup.number().required(),
        redisPw: yup.string().required(),
        redisCommandTimeout: yup.number().required(),

        // BaseStore aware
        algoliaAppId: yupBaseStoreAware(yup.string().required()),
        algoliaApiKey: yupBaseStoreAware(yup.string().required()),
        referer: yupBaseStoreAware(yup.string().required()),
        featureToggles: yupBaseStoreAware(
            yup.object({
                reviewsEnabled: yup.boolean().required()
            })
        )
    });

export type BffConfig = yup.InferType<typeof BffConfigSchema>;
